import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

// Al crear un contacto, Chatwoot le abre una "bandeja de contacto" en el
// canal que se le indique. No vale cualquiera: cada canal saca de un sitio
// distinto el identificador con el que va a alcanzarlo (ContactInboxBuilder),
// así que aquí se recoge qué canales admiten un contacto nuevo y qué dato
// exige cada uno. Sin ese dato el alta falla entera, no a medias.
const CHANNEL_REQUIREMENT = {
  'Channel::Api': null,
  'Channel::WebWidget': null,
  'Channel::Email': 'email',
  'Channel::Sms': 'phoneNumber',
  'Channel::Whatsapp': 'phoneNumber',
  'Channel::Evolution': 'phoneNumber',
  'Channel::TwilioSms': 'phoneNumber',
};

// El tipo de canal llega como `channelType` o `channel_type` según el
// getter del que venga la bandeja: los dos existen en el store.
const channelTypeOf = inbox => inbox?.channelType ?? inbox?.channel_type;

export const canHostNewContacts = inbox =>
  Object.hasOwn(CHANNEL_REQUIREMENT, channelTypeOf(inbox) ?? '');

// El dato que el canal necesita del contacto: null si se apaña solo,
// undefined si el canal no admite contactos nuevos. Sin el guardia, un canal
// que no pide nada guarda null y `??` lo colapsaría a undefined.
export const contactInboxRequirement = inbox =>
  canHostNewContacts(inbox)
    ? CHANNEL_REQUIREMENT[channelTypeOf(inbox)]
    : undefined;

export function useContactInboxes() {
  const inboxes = useMapGetter('inboxes/getAllInboxes');
  // El ajuste vive en la cuenta que sirve la API, no en la que trae el
  // usuario: esa sólo lleva su rol y su disponibilidad, sin `settings`.
  const { currentAccount } = useAccount();

  const availableInboxes = computed(() =>
    (inboxes.value || [])
      .filter(canHostNewContacts)
      .toSorted((a, b) => a.name.localeCompare(b.name))
  );

  const defaultInboxId = computed(() => {
    const configured = currentAccount.value?.settings?.default_contact_inbox_id;
    const id = Number(configured);
    if (!id) return null;
    return availableInboxes.value.some(inbox => inbox.id === id) ? id : null;
  });

  // Qué le falta al contacto para poder abrirse en ese canal: 'email',
  // 'phoneNumber' o nada.
  const missingFieldFor = (inbox, contact = {}) => {
    const required = contactInboxRequirement(inbox);
    if (!required) return null;
    return contact[required] ? null : required;
  };

  return { availableInboxes, defaultInboxId, missingFieldFor };
}
